import React, { useEffect, useState } from "react";
import { Marker, Popup } from "react-leaflet";
import L from "leaflet";
import api from "../../services/api";
import { API } from "../../App";

/*
  BranchMarkersLayer — shows every OTHER branch (not the one currently being
  viewed) on the live tracking map.

  Marker styling:
  - Default: white dot, orange border, branch code as the label (e.g. "E70").
  - E70 specifically: purple instead of orange/white.
  - Dual-brand (is_encon_insulation AND is_nevill_long both true): the outer
    border is rendered half-purple / half-orange instead of solid orange.

  Usage — drop this inside the existing map (TransportMapPanel.jsx), alongside
  the other <Marker> elements already rendered there, e.g.:

    import BranchMarkersLayer from './BranchMarkersLayer';
    ...
    <BranchMarkersLayer currentBranchId={currentBranchId} />

  Data source: GET /api/branch-directory (already exists — no new backend
  endpoint needed for reading). Requires each branch record to have
  `latitude`/`longitude` set — these don't exist yet on existing records and
  will need populating (geocode each branch's postcode once, e.g. via
  postcodes.io, then store lat/lng on the branch_directory record — a
  one-off backfill, separate from the weekly refresh job which only
  refreshes phone/email/postcode/brand flags).
*/

const ORANGE = "#ee7203";
const PURPLE = "#a855f7";

function buildIcon({ branchCode, isE70, isDualBrand }) {
  const borderStyle = isE70
    ? `border: 3px solid ${PURPLE};`
    : isDualBrand
    ? `border: 3px solid transparent; background-image: linear-gradient(white, white), linear-gradient(90deg, ${PURPLE} 50%, ${ORANGE} 50%); background-origin: border-box; background-clip: padding-box, border-box;`
    : `border: 3px solid ${ORANGE};`;

  const html = `
    <div style="
      width: 34px; height: 34px; border-radius: 9999px;
      background: white; ${borderStyle}
      display: flex; align-items: center; justify-content: center;
      box-shadow: 0 2px 6px rgba(0,0,0,0.35);
      font-family: Arial, sans-serif; font-size: 10px; font-weight: 700;
      color: ${isE70 ? PURPLE : "#334155"};
    ">${branchCode || "?"}</div>
  `;

  return L.divIcon({
    html,
    className: "",
    iconSize: [34, 34],
    iconAnchor: [17, 17],
    popupAnchor: [0, -17],
  });
}

export default function BranchMarkersLayer({ currentBranchId, activeStopPostcodes }) {
  const [branches, setBranches] = useState([]);

  useEffect(() => {
    api
      .get(`${API}/branch-directory`)
      .then((res) => setBranches(res.data || []))
      .catch(() => setBranches([]));
  }, []);

  // Only exclude the branch actually being viewed right now (matched by system branchCode,
  // e.g. "C30"/"E70") — most branch_directory records have no branchCode at all (they're not
  // linked to a system login), so they must still render regardless of currentBranchId.
  const others = branches.filter(
    (b) =>
      b.branchCode !== currentBranchId &&
      typeof b.latitude === "number" &&
      typeof b.longitude === "number"
  );

  return (
    <>
      {others.map((branch) => {
        const isE70 = branch.branchCode === "E70";
        const isDualBrand = !!branch.is_encon_insulation && !!branch.is_nevill_long;
        const normalizedPostcode = (branch.postcode || "").replace(/\s+/g, "").toUpperCase();
        // Dimmed by default — only a branch with an actual collection/delivery stop on the
        // current board stands out at full opacity, so the map isn't cluttered with branches
        // that have nothing to do with today's/this run's stops.
        const hasActiveStop = !!normalizedPostcode && activeStopPostcodes?.has(normalizedPostcode);

        return (
          <Marker
            key={branch.id}
            position={[branch.latitude, branch.longitude]}
            icon={buildIcon({ branchCode: branch.branchCode || (branch.nickname || branch.city || "?").slice(0, 3).toUpperCase(), isE70, isDualBrand })}
            opacity={hasActiveStop ? 1 : 0.6}
          >
            <Popup>
              <div style={{ fontFamily: "Arial, sans-serif", fontSize: 12, minWidth: 180 }}>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>
                  {branch.name} {branch.branchCode && `(${branch.branchCode})`}
                </div>
                <div>{branch.address1}</div>
                {branch.address2 && <div>{branch.address2}</div>}
                <div>
                  {branch.city}, {branch.postcode}
                </div>
                {branch.phone && <div>Tel: {branch.phone}</div>}
                {branch.email && <div>{branch.email}</div>}
                <div style={{ marginTop: 6, fontSize: 10, color: "#94a3b8" }}>
                  {branch.is_encon_insulation && "Encon Insulation"}
                  {branch.is_encon_insulation && branch.is_nevill_long && " · "}
                  {branch.is_nevill_long && "Nevill Long"}
                </div>
                {hasActiveStop && (
                  <div style={{ marginTop: 4, fontSize: 11, fontWeight: 700, color: "#16a34a" }}>
                    Collection/delivery here today
                  </div>
                )}
                {branch.last_refreshed && (
                  <div style={{ marginTop: 4, fontSize: 10, color: "#cbd5e1" }}>
                    Updated: {new Date(branch.last_refreshed).toLocaleDateString()}
                  </div>
                )}
              </div>
            </Popup>
          </Marker>
        );
      })}
    </>
  );
}
